/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CodecSelectorModal.tsx
 🗓️ Created: 2025-01-11
 🧩 Description:
    Modal component for selecting and ordering audio/video codecs
    with drag-and-drop functionality.

 🔧 Features:
    - Modal popup for codec selection
    - Drag-and-drop reordering
    - Separate audio and video codec lists
    - Clean, compact UI

 🔁 Last Updated: 2025-01-11 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect } from 'react';
import { X, GripVertical, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Modal from './Modal';
import { useTranslation } from 'react-i18next';

interface CodecSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
  videoOnly?: boolean;
  allowVideo?: boolean;
}

interface Codec {
  id: string;
  label: string;
  type: 'audio' | 'video';
}

const AVAILABLE_CODECS: Codec[] = [
  // Audio codecs
  { id: 'PCMU', label: 'PCMU (G.711 μ-law)', type: 'audio' },
  { id: 'PCMA', label: 'PCMA (G.711 A-law)', type: 'audio' },
  { id: 'G729', label: 'G.729', type: 'audio' },
  { id: 'G722', label: 'G.722 (HD Voice)', type: 'audio' },
  { id: 'OPUS', label: 'OPUS', type: 'audio' },
  { id: 'GSM', label: 'GSM', type: 'audio' },
  { id: 'iLBC', label: 'iLBC', type: 'audio' },
  // Video codecs
  { id: 'VP8', label: 'VP8', type: 'video' },
  { id: 'H264', label: 'H.264', type: 'video' },
  { id: 'H263', label: 'H.263', type: 'video' },
  { id: 'VP9', label: 'VP9', type: 'video' },
];

export function CodecSelectorModal({
  isOpen,
  onClose,
  value,
  onChange,
  videoOnly = false,
  allowVideo = true
}: CodecSelectorModalProps) {
  const { t } = useTranslation(['gatewaySipProfiles', 'common']);
  
  const [selectedCodecs, setSelectedCodecs] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Get available codecs based on props
  const availableCodecs = AVAILABLE_CODECS.filter(codec => {
    if (videoOnly) return codec.type === 'video';
    if (!allowVideo) return codec.type === 'audio';
    return true;
  });

  // Parse initial value
  useEffect(() => {
    if (value) {
      const codecs = value.split(',').map(c => c.trim()).filter(Boolean);
      setSelectedCodecs(codecs);
    } else {
      setSelectedCodecs([]);
    }
  }, [value]);

  const handleAddCodec = (codecId: string) => {
    if (!selectedCodecs.includes(codecId)) {
      const newSelected = [...selectedCodecs, codecId];
      setSelectedCodecs(newSelected);
    }
  };

  const handleRemoveCodec = (codecId: string) => {
    const newSelected = selectedCodecs.filter(c => c !== codecId);
    setSelectedCodecs(newSelected);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newSelected = [...selectedCodecs];
    const draggedItem = newSelected[draggedIndex];
    newSelected.splice(draggedIndex, 1);
    newSelected.splice(dropIndex, 0, draggedItem);
    
    setSelectedCodecs(newSelected);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setIsDragging(false);
    setDragOverIndex(null);
  };

  const handleSave = () => {
    onChange(selectedCodecs.join(','));
    onClose();
  };

  const handleCancel = () => {
    // Revert to original value
    if (value) {
      const codecs = value.split(',').map(c => c.trim()).filter(Boolean);
      setSelectedCodecs(codecs);
    } else {
      setSelectedCodecs([]);
    }
    onClose();
  };

  const getCodecLabel = (codecId: string) => {
    return AVAILABLE_CODECS.find(c => c.id === codecId)?.label || codecId;
  };

  const unselectedCodecs = availableCodecs.filter(
    codec => !selectedCodecs.includes(codec.id)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={videoOnly ? t('gatewaySipProfiles:selectVideoCodecs') : t('gatewaySipProfiles:selectAudioCodecs')}
      size="sm"
    >
      <div className="space-y-4">
        {/* Selected Codecs (Ordered) */}
        <div>
          <h3 className="text-xs font-medium text-foreground mb-2">
            {t('gatewaySipProfiles:selectedCodecs')} ({selectedCodecs.length})
          </h3>
          <div className={`
            space-y-1 min-h-[80px] bg-muted/30 rounded p-1.5
            transition-colors duration-150
            ${isDragging ? 'ring-1 ring-primary/30 bg-primary/5' : ''}
          `}>
            {selectedCodecs.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                {t('gatewaySipProfiles:noCodecsSelected')}
              </p>
            ) : (
              <AnimatePresence>
                {selectedCodecs.map((codecId, index) => (
                  <React.Fragment key={codecId}>
                    {/* Drop indicator line */}
                    {isDragging && draggedIndex !== null && draggedIndex !== index && dragOverIndex === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 2 }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-primary h-0.5 rounded-full mx-2"
                      />
                    )}
                    <motion.div
                    key={codecId}
                    data-codec={codecId}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ 
                      opacity: draggedIndex === index ? 0 : 1
                    }}
                    exit={{ opacity: 0 }}
                    transition={{ 
                      duration: 0.15,
                      layout: { duration: 0.15 }
                    }}
                    whileDrag={{ 
                      scale: 1.05,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
                      zIndex: 999,
                      opacity: 1
                    }}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center gap-1 px-1.5 py-1 bg-background border border-border rounded text-xs
                      cursor-move hover:border-primary/50 transition-colors duration-150
                      ${dragOverIndex === index && isDragging && draggedIndex !== index ? 'ring-1 ring-primary/30 bg-primary/5' : ''}
                    `}
                  >
                    <GripVertical className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" />
                    <span className="flex-1 text-foreground">
                      {index + 1}. {getCodecLabel(codecId)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveCodec(codecId)}
                      className="p-0.5 hover:bg-destructive/10 rounded text-destructive transition-colors"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                    </motion.div>
                  </React.Fragment>
                ))}
              </AnimatePresence>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            {t('gatewaySipProfiles:dragToReorder')}
          </p>
        </div>

        {/* Available Codecs */}
        {unselectedCodecs.length > 0 && (
          <div>
            <h3 className="text-xs font-medium text-foreground mb-2">
              {t('gatewaySipProfiles:availableCodecs')}
            </h3>
            <div className="grid grid-cols-3 gap-1 max-h-[200px] overflow-y-auto">
              <AnimatePresence>
                {unselectedCodecs.map((codec) => (
                  <button
                    key={codec.id}
                    type="button"
                    onClick={() => handleAddCodec(codec.id)}
                    className="
                      flex items-center gap-0.5 px-1.5 py-1 text-xs
                      bg-background border border-border rounded
                      hover:border-primary hover:bg-primary/5
                      transition-colors text-left
                    "
                  >
                    <Plus className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-foreground truncate" title={codec.label}>{codec.label}</span>
                  </button>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <button
            type="button"
            onClick={handleCancel}
            className="px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted rounded transition-colors"
          >
            {t('common:cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded transition-colors"
          >
            {t('common:save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

